import { Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { CloseIcon } from '@truvhq/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { useOnboardingState } from 'entities/onboarding/api/useOnboardingState';
import { useAssistantEnabled } from 'features/onboarding/lib/useAssistantEnabled';
import { useAutoOpenOnboarding } from 'features/onboarding/lib/useAutoOpenOnboarding';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { ChecklistPane } from 'features/onboarding/ui/ChecklistPane';
import { OnboardingConversation } from 'features/onboarding/ui/OnboardingConversation';

import ChevronDoubleRightIcon from './icons/chevron-double-right.svg?react';

const PANEL_TITLE = 'Set up your account';
const HIDE_LABEL = 'Hide setup';
// The design's easing, shared with the page-width transition in app/styles/style.css so the panel
// and the content it displaces move on one curve.
const SLIDE = { duration: 0.24, ease: [0.22, 0.7, 0.3, 1] } as const;

// Mounts the conversational onboarding as a global popup driven by useOnboardingDialogStore.
// Opening it automatically the first time is useAutoOpenOnboarding's job; after that the user opens
// it manually via the launcher.
//
// This is mounted in App.tsx outside AuthGuard, so it relies on useAssistantEnabled to check app
// access — otherwise it renders over the "Account pending" screen and calls endpoints that answer
// 403 for those users.
export function OnboardingDialog() {
  const canUseAssistant = useAssistantEnabled();
  const isOpen = useOnboardingDialogStore((state) => state.isOpen);
  const sessionKey = useOnboardingDialogStore((state) => state.sessionKey);
  const close = useOnboardingDialogStore((state) => state.close);
  const prefersReducedMotion = useReducedMotion();

  // Render mode comes from constance (via the onboarding state endpoint): "docked" is a right-side
  // panel that the page makes room for; "window" is the centered dialog. Preload it (not gated on
  // isOpen) so the layout is known before the dialog opens — otherwise a docked chat would briefly
  // flash the window mode while the request resolved.
  const { data: onboardingState, isLoading: isLayoutLoading } = useOnboardingState({ enabled: canUseAssistant });
  const isDocked = onboardingState?.layout === 'docked';

  useAutoOpenOnboarding({ enabled: canUseAssistant, onboardingState });

  // Header, checklist and conversation are the same in both containers; only the frame differs.
  const panel = (
    <div className="flex h-full flex-col">
      <div className="flex flex-none items-center gap-3 border-grey30 border-b px-6 py-3.5">
        <Typography className="min-w-0 flex-1 font-bold text-[1.375rem] text-grey90 leading-tight">
          {PANEL_TITLE}
        </Typography>
      </div>
      <ChecklistPane>
        {({ overlay, onAsk }) => (
          <OnboardingConversation
            key={sessionKey}
            overlay={overlay}
            onAsk={onAsk}
            leadingAction={
              <Tooltip title={HIDE_LABEL} placement="top">
                <IconButton
                  aria-label={HIDE_LABEL}
                  onClick={close}
                  sx={{
                    // Centred on the panel's left edge, so half of it sits over the page.
                    position: 'absolute',
                    left: -16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 32,
                    height: 32,
                    padding: 0,
                    color: 'grey.600',
                    backgroundColor: 'common.white',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    '&:hover': { borderColor: 'grey.400', backgroundColor: 'common.white' },
                  }}
                >
                  {isDocked ? <ChevronDoubleRightIcon width={20} height={20} /> : <CloseIcon width={18} height={18} />}
                </IconButton>
              </Tooltip>
            }
          />
        )}
      </ChecklistPane>
    </div>
  );

  if (!canUseAssistant) {
    return null;
  }

  // Hold off rendering a container until the layout is resolved, so a docked chat never flashes the
  // window mode first. Preloaded above, so this is settled before the user can open the dialog.
  if (isLayoutLoading) {
    return null;
  }

  if (isDocked) {
    // AnimatePresence keeps the panel mounted through its exit, so closing slides it off the right
    // edge instead of blinking out.
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="dialog"
            aria-label={PANEL_TITLE}
            className="fixed inset-y-0 right-0 z-[1300] flex w-assistant-panel flex-col border-grey30 border-l bg-white shadow-2xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={prefersReducedMotion ? { duration: 0 } : SLIDE}
          >
            {panel}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      aria-label={PANEL_TITLE}
      maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 720, height: '85vh', borderRadius: '12px' } }}
    >
      {panel}
    </Dialog>
  );
}
